import React, { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { api } from "../../lib/api";
import { Kpi, RangePicker, ExportButtons, Breakdown, money } from "../../components/ReportKit";

const ROLE_COLORS = { couple: "#C8A96B", planner: "#111111", vendor: "#8a6d35", therapist: "#b9a58a", admin: "#ccc" };

function Section({ title, exportSection, days, children }) {
  return (
    <section className="space-y-3" data-testid={`admin-report-${exportSection}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-serif text-2xl">{title}</h2>
        <ExportButtons path={`/admin/platform-reports/export?section=${exportSection}&days=${days}`} testid={`admin-export-${exportSection}`} />
      </div>
      {children}
    </section>
  );
}

export default function PlatformReportsPage() {
  const [days, setDays] = useState(90);
  const [r, setR] = useState(null);
  useEffect(() => { setR(null); api.get(`/admin/platform-reports?days=${days}`).then((x) => setR(x.data)).catch(() => setR(false)); }, [days]);
  const roles = r ? Object.keys(r.signups.all_users_by_role) : [];

  return (
    <div className="max-w-6xl space-y-8" data-testid="admin-reports-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <p className="font-script text-[var(--gold)] text-3xl">Platform</p>
          <h1 className="font-serif font-light text-4xl">Reports</h1>
        </div>
        <div className="flex flex-wrap gap-3 items-center"><RangePicker days={days} setDays={setDays} /><ExportButtons path="/admin/platform-reports/export?section=users" testid="admin-export-users" /></div>
      </div>
      {r === null && <div className="h-40 rounded-2xl bg-white/70 animate-pulse" />}
      {r === false && <p className="text-sm text-red-600">Couldn't load reports.</p>}
      {r && (
        <>
          <Section title="Subscriptions" exportSection="subscriptions" days={days}>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <Kpi testid="kpi-mrr" label="Monthly revenue (MRR)" value={money(r.subscriptions.mrr)} sub="Active paid plans × plan price" />
              <Kpi testid="kpi-active-paid" label="Active paid" value={r.subscriptions.active_paid} />
              <Kpi testid="kpi-new-paid" label="New paid" value={r.subscriptions.new_paid} sub={`last ${days} days`} />
              <Kpi testid="kpi-cancellations" label="Cancellations" value={r.subscriptions.cancellations} sub={`last ${days} days`} />
            </div>
            <Breakdown testid="admin-by-plan" title="Users by plan" data={r.subscriptions.by_plan} />
          </Section>

          <Section title="New signups" exportSection="signups" days={days}>
            <div className="grid lg:grid-cols-[1fr_300px] gap-3">
              <div className="bg-white border border-[var(--line)] rounded-2xl p-5 h-72" data-testid="admin-signups-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={r.signups.series} margin={{ left: -20 }}>
                    <CartesianGrid stroke="#eee6d8" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} minTickGap={24} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                    <Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} />
                    {roles.map((k) => <Bar key={k} dataKey={k} stackId="a" fill={ROLE_COLORS[k] || "#999"} />)}
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <Breakdown testid="admin-signups-by-role" title={`${r.signups.total} signups by type`} data={r.signups.by_role} />
            </div>
          </Section>

          <Section title="Services" exportSection="services" days={days}>
            <div className="grid md:grid-cols-3 gap-3">
              <Breakdown testid="admin-svc-agreements" title="Agreements" data={r.services.agreements} />
              <Breakdown testid="admin-svc-therapy" title="Therapy sessions" data={r.services.therapy_sessions} />
              <Breakdown testid="admin-svc-live" title="Live streams" data={r.services.live_broadcasts} />
              <Breakdown testid="admin-svc-podcast" title="Podcast" data={{ episodes: r.services.podcast.episodes, ...r.services.podcast.shows }} />
              <Breakdown testid="admin-svc-community" title="Community" data={r.services.community} />
            </div>
          </Section>

          <Section title="Payments" exportSection="payments" days={days}>
            <div className="grid md:grid-cols-[260px_1fr_1fr] gap-3">
              <Kpi testid="kpi-gross" label="Gross payments" value={money(r.payments.gross)} sub={`${r.payments.count} payments`} />
              <Breakdown testid="admin-pay-kind" title="By product" data={r.payments.by_kind} />
              <Breakdown testid="admin-pay-role" title="By payer (vendors, planners, couples)" data={r.payments.by_payer_role} />
            </div>
          </Section>
        </>
      )}
    </div>
  );
}
